import plusIcon from "../../assets/icons/plus.svg";
import { Trash2 } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { showSuccess, showError } from "../../components/ui/toast";
import { getToken, getUser } from "../../services/authStore";
import "./type.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function TypeModule() {
  const token = getToken();
  const user = getUser();

  const [types, setTypes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isFirstSave, setIsFirstSave] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const role = user?.role || "";

  const canShowGlobalToggle =
    role === "super_admin" || role === "support_admin";

  // =============================
  // FETCH TYPES
  // GET /settings/types/{company_id}/{role}
  // =============================
  const fetchTypes = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/settings/types/${user.company_id}/${role}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = res.data || [];

      setIsFirstSave(data.length === 0);

      const mapped = data.map((t) => ({
        id: t.id,
        name: t.name || "",
        color: t.colour
          ? t.colour.startsWith("#")
            ? t.colour
            : `#${t.colour.padStart(6, "0")}`
          : "#000000",
        status: t.status ?? 1,
        isglobal: t.isglobal ?? 0,
      }));

      setTypes(mapped);
    } catch (error) {
      console.error(error);
      showError("Failed to fetch types");
    }
  }, [token, user.company_id, role]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  // =============================
  // ADD ROW
  // =============================
  const handleAddRow = () => {
    setTypes((prev) => [
      ...prev,
      {
        id: null,
        name: "",
        color: "#000000",
        status: 1,
        isglobal: 0,
      },
    ]);
  };

  // =============================
  // UPDATE FIELD
  // =============================
  const updateField = (index, key, value) => {
    setTypes((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    );
  };

  // =============================
  // DELETE ROW
  // DELETE /settings/types/{company_id}/{type_id}/{role}
  // =============================
  const handleDeleteRow = async (index) => {
    const type = types[index];

    if (!type) return;

    if (!type.id) {
      setTypes((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    if (!window.confirm("Are you sure you want to delete this type?")) return;

    try {
      await axios.delete(
        `${API_URL}/settings/types/${user.company_id}/${type.id}/${role}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setTypes((prev) => prev.filter((_, i) => i !== index));
      showSuccess("Deleted Successfully");
    } catch (error) {
      console.error("Delete error:", error);

      let errorMessage = "Delete Failed";

      if (error?.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map((d) => d.msg)
            .join(", ");
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showError(errorMessage);
    }
  };

  // =============================
  // SAVE LOGIC
  // POST IF FIRST TIME → ELSE PUT
  // =============================
  const handleSave = async () => {
    if (types.some((t) => !t.name.trim())) {
      showError("Name cannot be empty");
      return;
    }

    setIsSaving(true);

    try {
      let response;

      if (isFirstSave) {
        const payload = types.map((t) => ({
          name: t.name.trim(),
          colour: t.color.replace("#", ""),
          status: t.status ?? 0,
          isglobal: t.isglobal ?? 0,
        }));

        response = await axios.post(
          `${API_URL}/settings/types/${user.company_id}/${role}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setIsFirstSave(false);
        showSuccess("Types saved successfully");
      } else {
        const payload = types.map((t) => ({
          id: t.id ?? 0,
          name: t.name.trim(),
          colour: t.color.replace("#", ""),
          status: t.status ?? 0,
          isglobal: t.isglobal ?? 0,
        }));

        response = await axios.put(
          `${API_URL}/settings/types/${user.company_id}/${role}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        showSuccess("Types updated successfully");
      }
      const updated = (response.data || []).map((t) => ({
        id: t.id,
        name: t.name || "",
        color: t.colour
          ? t.colour.startsWith("#")
            ? t.colour
            : `#${t.colour.padStart(6, "0")}`
          : "#000000",
        status: t.status ?? 1,
        isglobal: t.isglobal ?? 0,
      }));

      setTypes(updated);
    } catch (error) {
      console.error(error);
      showError("Save Failed");
    } finally {
      setIsSaving(false);
    }
  };

  const filteredTypes = types.filter((t) =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="type-wrapper">
      <div className="type-container">
        {/* HEADER */}
        <div className="type-header">
          <span className="type-title"></span>

          <div className="type-header-right">
            <input
              type="text"
              placeholder="Search type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="type-search"
            />

            <div className="type-add-btn" onClick={handleAddRow}>
              <img src={plusIcon} alt="add" />
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="type-table-wrapper">
          <div className="type-scroll-wrapper">
            <table className="type-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type Name</th>
                  <th>Color</th>
                  <th>Status</th>

                  {canShowGlobalToggle && <th>Is Global</th>}

                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredTypes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canShowGlobalToggle ? 6 : 5}
                      className="table-empty-msg"
                    >
                      No types available
                    </td>
                  </tr>
                ) : (
                  filteredTypes.map((t, index) => (
                    <tr key={t.id || index}>
                      <td>{index + 1}</td>

                      <td>
                        <input
                          value={t.name}
                          onChange={(e) =>
                            updateField(index, "name", e.target.value)
                          }
                          placeholder="Enter type name"
                        />
                      </td>

                      <td>
                        <input
                          type="color"
                          value={t.color}
                          onChange={(e) =>
                            updateField(index, "color", e.target.value)
                          }
                        />
                      </td>

                      <td>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={t.status === 1}
                            onChange={(e) =>
                              updateField(
                                index,
                                "status",
                                e.target.checked ? 1 : 0,
                              )
                            }
                          />
                          <span className="slider"></span>
                        </label>
                      </td>

                      {canShowGlobalToggle && (
                        <td>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={t.isglobal === 1}
                              onChange={(e) =>
                                updateField(
                                  index,
                                  "isglobal",
                                  e.target.checked ? 1 : 0,
                                )
                              }
                            />
                            <span className="slider"></span>
                          </label>
                        </td>
                      )}

                      <td>
                        <Trash2
                          size={18}
                          className="type-action-icon delete"
                          onClick={() => handleDeleteRow(index)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ textAlign: "right", padding: "20px" }}>
          <button className="save-btn" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
